/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useMemo } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useCircles, useMe, useVerticals } from '@/queries/session';
import { useBoardTasks } from '@/queries/tasks';
import { BOARD_SORTS, applyFilters, useBoardFilters, type BoardSort } from './useBoardFilters';
import { PRIORITIES } from '@/types/task';
import { Tip } from '@/app/Tip';
import { cn } from '@/lib/cn';

/**
 * The board's own controls: how many tasks are showing, Clear, and Filters.
 *
 * These live in the topbar but need the board's data. They read it from the same
 * React Query key the board uses, so there is no second request — the cache is
 * the shared state, which is the thing that replaces four module-level variables
 * and a manual `syncBoardSearch()`.
 *
 * The count and Clear sit on the far side of the row from the search on purpose:
 * both change width as somebody types — "9 tasks" becoming "12 of 140", Clear
 * arriving with the first keystroke — and either would shove New task about
 * mid-search.
 *
 * **Filters stay on every view; the count and Clear are the board's alone.** The
 * List view drives the same three filters from its own inline selects and reads
 * this popover's state back — `list-view` checks that changing either updates the
 * other — so removing this off the board leaves that view's controls bound to
 * nothing. The tally is different: the List and Archived views each count their
 * own rows, and two numbers for two different things is how they get read as one.
 * `syncBoardSearch()` drew exactly this line, by emptying the count's text rather
 * than taking the element away.
 */
export function BoardControls({ onBoard, hideFilters }: { onBoard: boolean; hideFilters: boolean }) {
  const { data: circles } = useCircles();
  const { data: tasks } = useBoardTasks(circles?.activeId ?? '', circles !== undefined);
  const { filters, setFilter, clear, activeCount } = useBoardFilters();
  const columns = useVerticals();
  const { data: me } = useMe();

  const total = tasks?.length ?? 0;
  const shown = useMemo(() => applyFilters(tasks ?? [], filters, columns).length, [tasks, filters, columns]);

  // Options are derived from the tasks actually on this board, not from every tag
  // and person in the system: offering a filter that can only ever return nothing
  // is worse than not offering it. The signed-in person is pulled out of this
  // list — they get their own "Me" option above it instead of a second,
  // identical-looking entry under their own name.
  const owners = useMemo(() => {
    const seen = new Set<string>();
    for (const t of tasks ?? []) for (const o of [...t.assignees, ...t.participants]) seen.add(o);
    if (me?.name) seen.delete(me.name);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [tasks, me?.name]);

  // **Grouped by circle, because two circles may now have the same tag name.**
  // Scoping tags means "Urgent" in Core and "Urgent" in Sales are two different
  // labels; listed flat they are two identical-looking options and picking one is
  // a coin toss. The circle comes from the task the tag was found on — a task's
  // tags belong to its home circle by construction — so nothing extra is fetched.
  //
  // In a single circle every tag has the same heading, so the grouping collapses
  // to what it always was and `<optgroup>` is not rendered at all.
  const tagGroups = useMemo(() => {
    const byCircle = new Map<string, { circle: string; tags: { id: string; name: string }[] }>();
    const seen = new Set<string>();
    for (const t of tasks ?? []) {
      const circle = t.circle?.name ?? '';
      for (const tag of t.tags) {
        if (seen.has(tag.id)) continue;
        seen.add(tag.id);
        if (!byCircle.has(circle)) byCircle.set(circle, { circle, tags: [] });
        byCircle.get(circle)!.tags.push(tag);
      }
    }
    for (const g of byCircle.values()) g.tags.sort((a, b) => a.name.localeCompare(b.name));
    return [...byCircle.values()].sort((a, b) => a.circle.localeCompare(b.circle));
  }, [tasks]);

  return (
    <>
      {/* Always in the DOM, empty when it has nothing to say — its text is the
          assertion, and an element that comes and goes makes "no count here" and
          "the count has not rendered yet" the same observation. */}
      <span id="board-count" className="board-task-count hidden whitespace-nowrap text-xs text-ink-muted phone:inline">
        {!onBoard || total === 0
          ? ''
          : shown === total
            ? `${total} task${total === 1 ? '' : 's'}`
            : `${shown} of ${total}`}
      </span>

      {onBoard && activeCount > 0 && (
        <button
          id="board-clear"
          onClick={clear}
          className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-semibold text-ink-muted transition-colors duration-fast ease-out-soft hover:border-border-strong hover:text-ink"
        >
          Clear
        </button>
      )}

      {/* Hidden, not unmounted — `.topbar.todo-mode .filter-wrap{display:none}`
          is the vanilla rule and this is the same one. My Day is not the board
          and the board's filters do not reach it, so leaving them on screen
          would offer a control that quietly does nothing; but the List view
          reads these same selects while they are shut, so they have to stay in
          the page. */}
      <div className="filter-wrap relative flex items-center" style={hideFilters ? { display: 'none' } : undefined}>
      <Popover.Root>
        {/* `Tip` rather than the `title` this carried: the word "Filters" is
            hidden below `phone`, and a native tooltip is both the slowest way to
            answer "what is this icon" and the one a keyboard never sees. */}
        <Tip label="Filters">
          <Popover.Trigger asChild>
            <button
              id="filter-btn"
              aria-label="Filters"
              className={cn(
                'tap flex h-[34px] shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-semibold transition-colors duration-fast ease-out-soft',
                activeCount > 0
                  ? 'border-action bg-action-bg text-action'
                  : 'border-border bg-surface text-ink-muted hover:border-border-strong hover:text-ink',
              )}
            >
              <SlidersHorizontal className="size-4 shrink-0" aria-hidden />
              <span className="hidden phone:inline">Filters</span>
              {/* The count stays even when the word goes: it is the only thing
                  that says a filter is on, and a hidden filter is how a board
                  ends up looking empty for no visible reason. */}
              <span
                id="filter-count"
                style={{ display: activeCount > 0 ? 'flex' : 'none' }}
                className="size-[17px] items-center justify-center rounded-full bg-action text-2xs font-bold text-bg"
              >
                {activeCount}
              </span>
            </button>
          </Popover.Trigger>
        </Tip>

        {/* **Not `forceMount`.** It was, so the List view's tests could read
            `#vertical-filter` while the panel was shut — and that cost far more
            than it bought. A force-mounted Radix popover registers a dismissable
            layer *even when closed*, Escape is delivered to the topmost layer
            only, and this closed panel sat above the task modal and ate the key.
            Anybody with a task open and a circle switcher on screen could not
            close it with Escape; the ✕ still worked, so it read as "Escape is
            broken sometimes".

            Found by pressing Escape, not by reading anything. The assertions
            that wanted the selects now open the panel first, which is what a
            person has to do too. */}
        <Popover.Portal>
          <Popover.Content
            id="filter-panel"
            align="end"
            sideOffset={8}
            className="motion-surface z-50 w-[250px] rounded-xl border border-border bg-surface p-3 shadow-overlay"
          >
            <Field label="Vertical">
              <Select id="vertical-filter" value={filters.vertical} onChange={(v) => setFilter({ vertical: v })}>
                <option value="">All verticals</option>
                {columns.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </Select>
            </Field>

            <Field label="Participant">
              <Select id="owner-filter" value={filters.owner} onChange={(v) => setFilter({ owner: v })}>
                <option value="">All participants</option>
                {me?.name && <option value={me.name}>Me ({me.name})</option>}
                {owners.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </Select>
            </Field>

            <Field label="Priority">
              <Select id="priority-filter" value={filters.priority} onChange={(v) => setFilter({ priority: v })}>
                <option value="">All priorities</option>
                {PRIORITIES.map((p) => (
                  <option key={p.id} value={String(p.id)}>{p.label} — {p.name}</option>
                ))}
              </Select>
            </Field>

            <Field label="Tag">
              <Select id="tag-filter" value={filters.tag} onChange={(v) => setFilter({ tag: v })}>
                <option value="">All tags</option>
                {/* One group per circle in the combined view; a single circle has
                    exactly one group and the heading would be noise, so it is
                    dropped rather than repeated above every option. */}
                {tagGroups.length > 1
                  ? tagGroups.map((g) => (
                    <optgroup key={g.circle} label={g.circle || 'Circle'}>
                      {g.tags.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </optgroup>
                  ))
                  : tagGroups[0]?.tags.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </Select>
            </Field>

            {/* A toggle, not a select: this is the one filter meant to be a single
                click, matching the "in two clicks or fewer" goal it exists for. */}
            <label className="mb-2.5 flex items-center gap-2 text-sm text-ink">
              <input
                id="overdue-filter"
                type="checkbox"
                checked={filters.overdue}
                onChange={(e) => setFilter({ overdue: e.target.checked })}
                className="size-4 rounded border-border"
              />
              Overdue only
            </label>

            {/* Order, not a filter: it changes what comes first, never what is on
                the board, so it does not count towards the badge or light up Clear. */}
            <Field label="Board order">
              <Select id="board-sort" value={filters.sort} onChange={(v) => setFilter({ sort: v as BoardSort })}>
                {BOARD_SORTS.map((s) => (
                  <option key={s} value={s}>{SORT_LABEL[s]}</option>
                ))}
              </Select>
            </Field>

            {activeCount > 0 && (
              <button
                onClick={clear}
                className="mt-1 w-full rounded-lg border border-border py-1.5 text-xs font-semibold text-ink-muted hover:border-border-strong hover:text-ink"
              >
                Clear all
              </button>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      </div>
    </>
  );
}

const SORT_LABEL: Record<BoardSort, string> = {
  due: 'Due date — soonest first',
  priority: 'Priority — P1 first',
  title: 'Title — A → Z',
  manual: 'Manual — drag order',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-2.5 block">
      <span className="mb-1 block text-2xs font-semibold uppercase tracking-wide text-ink-faint">{label}</span>
      {children}
    </label>
  );
}

function Select({
  id,
  value,
  onChange,
  children,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-sm"
    >
      {children}
    </select>
  );
}
